import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Divider,
  IconButton,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { OverflowTooltip } from 'components/common/overflow-tooltip';
import { useAuthContext } from 'providers/auth';
import { FC, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  StoredAccount,
  activateAccount,
  clearPoolTokens,
  defaultLabelForEmail,
  getAccounts,
  getActiveAccountConfig,
  getActiveClientId,
  getFallbackAccount,
  isLabelInUse,
  removeAccount,
  upsertAccount,
} from 'utils/accounts';
import { startAddAccount } from 'utils/accounts-add';
import { clearAllAccountsAndTokens } from 'utils/accounts-logout';
import messages from './messages';
import { NameAccountDialog } from './name-account-dialog';
import { RemoveAccountDialog } from './remove-account-dialog';

const MUTED = '#ffffff8c';
const ACTIVE_BAR = '#dd2424';
// Solid approximation of the popover bg + hover overlay — used behind the (absolute) row actions
// so the revealed rename/remove buttons sit on a clean strip instead of over the label text.
const ROW_HOVER_BG = '#2c2c2c';

export const AccountSwitcher: FC = () => {
  const { formatMessage } = useIntl();
  const { user } = useAuthContext();
  const activeEmail = user.email || '';

  // Snapshot of the registry; re-read from localStorage after a mutation (rename / remove).
  const [accounts, setAccounts] = useState(getAccounts);
  const refresh = () => setAccounts(getAccounts());

  const activeClientId = getActiveClientId();

  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  // Escape cancels the inline rename by unmounting the field. Some browsers fire `blur` on the
  // field as it's removed, which would run commitRename and save the discarded value — this flag
  // lets commitRename recognize that trailing blur and skip it.
  const cancellingRename = useRef(false);
  const [namingCurrentOpen, setNamingCurrentOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState<StoredAccount | null>(null);

  const switchTo = (account: StoredAccount) => {
    if (account.clientId === activeClientId) return;
    activateAccount(account);
    // Go home (a full load, to re-init Amplify) rather than reload the current route — account
    // types (internal/admin vs external/client) have different route trees, so the current inner
    // route may not exist for the target account.
    window.location.href = '/';
  };

  const remove = (account: StoredAccount) => {
    clearPoolTokens(account.clientId);
    removeAccount(account.clientId);
    if (account.clientId !== activeClientId) {
      refresh();
      return;
    }
    // Removed the active account: fall back to another one, or out to login if none remain.
    const next = getFallbackAccount(account.clientId);
    if (next) {
      switchTo(next);
    } else {
      clearAllAccountsAndTokens();
      window.location.href = '/login';
    }
  };

  const isLabelTaken = (label: string, exceptClientId: string) =>
    isLabelInUse(
      label,
      accounts.filter((a) => a.clientId !== exceptClientId).map((a) => a.label),
    );

  const commitRename = (account: StoredAccount) => {
    // A blur fired while cancelling (Escape) must not persist the discarded value.
    if (cancellingRename.current) {
      cancellingRename.current = false;
      return;
    }
    const label = editValue.trim();
    if (label && !isLabelTaken(label, account.clientId)) {
      upsertAccount({ ...account, label });
    }
    setEditingClientId(null);
    refresh();
  };

  const cancelRename = () => {
    cancellingRename.current = true;
    setEditingClientId(null);
  };

  const startRename = (account: StoredAccount) => {
    cancellingRename.current = false;
    setEditingClientId(account.clientId);
    setEditValue(account.label);
  };

  const addAccount = () => {
    const current = getActiveAccountConfig(activeEmail);
    const currentRegistered = !!current && accounts.some((a) => a.clientId === current.clientId);
    // First add for this account: name the one we're on before leaving, then continue the flow.
    if (current && !currentRegistered) {
      setNamingCurrentOpen(true);
    } else {
      startAddAccount(activeEmail);
    }
  };

  const nameCurrentAndAdd = (label: string) => {
    const current = getActiveAccountConfig(activeEmail);
    if (current) upsertAccount({ ...current, label });
    setNamingCurrentOpen(false);
    startAddAccount(activeEmail);
  };

  return (
    <Box sx={{ color: '#fff', py: 1 }}>
      {accounts.length > 1 && (
        <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: 11, letterSpacing: 1, color: MUTED }}>
          {formatMessage(messages.switchAccountHeading).toUpperCase()}
        </Typography>
      )}

      <Stack>
        {accounts.map((account) => {
          const isActive = account.clientId === activeClientId;
          const isEditing = editingClientId === account.clientId;
          const renameDuplicate =
            isEditing && editValue.trim() !== '' && isLabelTaken(editValue, account.clientId);
          return (
            <Box
              key={account.clientId}
              data-account-row
              sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                color: isActive ? '#fff' : MUTED,
                '&:hover, &:focus-within': { backgroundColor: ROW_HOVER_BG },
                '&:hover .account-row-actions, &:focus-within .account-row-actions': {
                  opacity: 1,
                  pointerEvents: 'auto',
                },
              }}
            >
              {isActive && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    backgroundColor: ACTIVE_BAR,
                  }}
                />
              )}
              <ListItemButton
                onClick={() => !isEditing && switchTo(account)}
                sx={{ pl: 2, minWidth: 0, flex: 1, color: 'inherit' }}
              >
                {isEditing ? (
                  <TextField
                    autoFocus
                    variant="standard"
                    value={editValue}
                    error={renameDuplicate}
                    onChange={(e) => setEditValue(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onBlur={() => commitRename(account)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !renameDuplicate) commitRename(account);
                      if (e.key === 'Escape') cancelRename();
                    }}
                    sx={{
                      '& .MuiInputBase-input': { color: '#fff' },
                      '& .MuiInput-underline:before': { borderBottomColor: '#ffffff59' },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: '#ffffff8c',
                      },
                    }}
                  />
                ) : (
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <OverflowTooltip>{account.label}</OverflowTooltip>
                    <OverflowTooltip typographyProps={{ sx: { color: '#ffffff59', fontSize: 11 } }}>
                      {account.email}
                    </OverflowTooltip>
                  </Box>
                )}
              </ListItemButton>
              {!isEditing && (
                <Box
                  className="account-row-actions"
                  sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: 'flex',
                    alignItems: 'center',
                    pl: 3,
                    opacity: 0,
                    pointerEvents: 'none',
                    transition: 'opacity .15s',
                    background: `linear-gradient(to right, transparent, ${ROW_HOVER_BG} 24px)`,
                  }}
                >
                  <IconButton
                    size="small"
                    aria-label={formatMessage(messages.rename)}
                    sx={{ color: MUTED }}
                    onClick={() => startRename(account)}
                  >
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={formatMessage(messages.remove)}
                    sx={{ color: MUTED, mr: 1 }}
                    onClick={() => setPendingRemoval(account)}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>

      <Divider sx={{ borderColor: '#ffffff1f' }} />

      <ListItemButton sx={{ pl: 2, color: '#ffffffb3' }} onClick={addAccount}>
        <AddIcon sx={{ fontSize: 18, mr: 1 }} />
        <ListItemText primary={formatMessage(messages.addAccount)} />
      </ListItemButton>

      <NameAccountDialog
        open={namingCurrentOpen}
        title={formatMessage(messages.nameCurrentTitle)}
        initialValue={defaultLabelForEmail(activeEmail)}
        emailHint={activeEmail}
        takenLabels={accounts.map((a) => a.label)}
        onSubmit={nameCurrentAndAdd}
        onCancel={() => setNamingCurrentOpen(false)}
      />

      <RemoveAccountDialog
        account={pendingRemoval}
        isActive={pendingRemoval?.clientId === activeClientId}
        fallbackLabel={
          pendingRemoval ? getFallbackAccount(pendingRemoval.clientId)?.label : undefined
        }
        onConfirm={() => {
          const account = pendingRemoval;
          setPendingRemoval(null);
          if (account) remove(account);
        }}
        onCancel={() => setPendingRemoval(null)}
      />
    </Box>
  );
};
